import { Link } from '@tanstack/react-router'
import type { ReactElement, ReactNode } from 'react'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { SOURCE_HEADER } from './sourceColours'

export const sectionLabelClass = 'text-etn-micro font-semibold uppercase tracking-[0.06em] text-etn-text-label'
export const thClass = 'h-9 whitespace-nowrap border-b border-[var(--etn-gray-100)] px-3.5 text-left text-etn-micro font-semibold uppercase tracking-[0.05em] text-etn-text-label'
export const tdClass = 'h-11 px-3.5 py-1.5 align-middle text-[13px]'
export const pillClass = (active: boolean) =>
  cn(
    'inline-flex h-7 items-center gap-1.5 rounded-etn-pill border px-2.5 text-[12.5px] font-medium',
    active ? 'border-transparent bg-etn-accent-tint font-semibold text-etn-accent-text-active' : 'border-etn-border-input bg-etn-surface text-etn-text'
  )

// A count beside a label, set apart in a small grey chip as on the dashboard tabs.
export const CountChip = ({ children }: { children: ReactNode }) => (
  <span className="rounded-etn-pill bg-[rgba(31,48,61,0.07)] px-1.5 text-[10.5px] font-semibold tabular-nums">{children}</span>
)

export function DashCard({
  title,
  subtitle,
  right,
  footLink,
  testId,
  className,
  source,
  children,
}: {
  title: ReactNode
  subtitle?: ReactNode
  right?: ReactNode
  footLink?: { to: string; label: string }
  testId?: string
  className?: string
  source?: 'call' | 'email'
  children: ReactNode
}) {
  return (
    <section className={cn('rounded-etn-card border border-etn-border-hairline bg-etn-surface shadow-etn-whisper', className)} data-testid={testId}>
      <div
        data-source={source}
        className={cn('flex flex-wrap items-center justify-between gap-3 border-b border-[var(--etn-gray-100)] px-[18px] py-3.5', source && SOURCE_HEADER[source])}
      >
        <div>
          <h3 className="text-[13.5px] font-semibold text-etn-text">{title}</h3>
          {subtitle && <div className="text-xs text-etn-text-soft">{subtitle}</div>}
        </div>
        {right}
      </div>
      {children}
      {footLink && (
        <Link
          to={footLink.to as never}
          className="block border-t border-[var(--etn-gray-100)] px-[18px] py-2.5 text-[12.5px] font-medium text-etn-accent-text-active hover:bg-etn-row-hover"
        >
          {footLink.label}
        </Link>
      )}
    </section>
  )
}

export function Kpi({
  testId,
  label,
  value,
  detail,
  tone,
  children,
}: {
  testId: string
  label: string
  value: ReactNode
  detail?: ReactNode
  tone?: 'warn' | 'good'
  children?: ReactNode
}) {
  const valueTone = tone === 'warn' ? 'text-etn-warning-text' : tone === 'good' ? 'text-etn-accent-text-active' : 'text-etn-text'
  return (
    <div className="rounded-etn-card border border-etn-border-hairline bg-etn-surface px-4 py-3.5 shadow-etn-whisper" data-testid={testId}>
      <div className={sectionLabelClass}>{label}</div>
      <div className={cn('mt-1.5 text-[28px] font-semibold leading-tight tabular-nums', valueTone)}>{value}</div>
      {detail && <div className="mt-0.5 truncate text-xs text-etn-text-soft">{detail}</div>}
      {children}
    </div>
  )
}

export const Dot = ({ colour, size = 8 }: { colour: string; size?: number }) => (
  <span aria-hidden className="inline-block shrink-0 rounded-full" style={{ width: size, height: size, background: colour }} />
)

// A hover label for a coloured mark: its colour, what it is and its count. Carries its own provider, as nothing above
// the dashboard mounts one.
export function Tip({ label, value, colour, children }: { label: string; value: ReactNode; colour?: string; children: ReactElement }) {
  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild>{children}</TooltipTrigger>
        <TooltipContent sideOffset={6} className="flex items-center gap-1.5">
          {colour && <Dot colour={colour} />}
          <span>{label}</span>
          <b className="font-semibold tabular-nums">{value}</b>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

export interface Segment {
  key: string
  label: string
  count: number
  colour: string
}

// Leave a 2px surface gap between segments so neighbouring colours never touch.
export function SegmentBar({ segments, height = 8, label }: { segments: Segment[]; height?: number; label: string }) {
  const total = segments.reduce((sum, segment) => sum + segment.count, 0)
  if (!total) return <div className="rounded-full bg-[var(--etn-gray-100)]" style={{ height }} aria-label={`${label}: none`} />
  return (
    <div className="flex gap-[2px] overflow-hidden rounded-full" style={{ height }} role="img" aria-label={label}>
      {segments
        .filter(segment => segment.count)
        .map(segment => (
          <Tip key={segment.key} label={segment.label} value={segment.count} colour={segment.colour}>
            <span aria-label={`${segment.label} ${segment.count}`} style={{ flexGrow: segment.count, flexBasis: 0, background: segment.colour }} />
          </Tip>
        ))}
    </div>
  )
}

export function Legend({ items }: { items: { key: string; label: string; colour: string }[] }) {
  return (
    <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11.5px] text-etn-text-label">
      {items.map(item => (
        <span key={item.key} className="inline-flex items-center gap-1.5">
          <Dot colour={item.colour} />
          {item.label}
        </span>
      ))}
    </div>
  )
}

export interface AreaRow {
  productArea: string | null
  count: number
  information: number
}

// Bars sized against the biggest area; the darker part is the share of information requests. Unassigned tickets get a
// line of their own so they never dwarf the real areas.
export function AreaBars({ areas, labelOf, limit }: { areas: AreaRow[]; labelOf: (key: string) => string; limit?: number }) {
  const rows = areas
    .filter(area => area.productArea !== null)
    .sort((left, right) => right.count - left.count)
    .slice(0, limit)
    .map(area => ({ key: area.productArea as string, label: labelOf(area.productArea as string), count: area.count, information: area.information }))
  const unassigned = areas.find(area => area.productArea === null)?.count ?? 0
  const max = Math.max(1, ...rows.map(row => row.count))
  return (
    <div className="space-y-2.5">
      {rows.map(row => (
        <div key={row.key}>
          <div className="mb-1 flex justify-between gap-3 text-[12.5px]">
            <span className="text-etn-text-cell">{row.label}</span>
            <span className="whitespace-nowrap">
              <b className="font-semibold text-etn-text">{row.count}</b> <span className="text-etn-text-soft">({row.information} information)</span>
            </span>
          </div>
          <div className="flex h-2.5 overflow-hidden rounded" style={{ width: `${(row.count / max) * 100}%` }}>
            {row.information > 0 && (
              <Tip label={`${row.label}: information requests`} value={row.information} colour="var(--etn-amber-500)">
                <span aria-label={`${row.label} information ${row.information}`} className="bg-[var(--etn-amber-500)]" style={{ width: `${(row.information / row.count) * 100}%` }} />
              </Tip>
            )}
            {row.count > row.information && (
              <Tip label={`${row.label}: everything else`} value={row.count - row.information} colour="var(--etn-amber-100)">
                <span aria-label={`${row.label} other ${row.count - row.information}`} className="flex-1 bg-etn-warning-subtle" />
              </Tip>
            )}
          </div>
        </div>
      ))}
      {unassigned > 0 && (
        <p className="pt-1 text-xs text-etn-text-soft">
          {unassigned} {unassigned === 1 ? 'ticket has' : 'tickets have'} no area yet
        </p>
      )}
    </div>
  )
}
